import {
  KPIDisplayProps,
  KPIDisplayType
} from "@/components/controls/kpiDisplayTypes";
import {
  ComparisonData,
  PeerComparisonData,
  ReportData,
  SettingsPeriod,
  SettingsPeriodString,
  SingleValueData
} from "./types";
import i18next, { TFunction } from "i18next";

export const formatPeriodCase = (period: string): string => {
  // Keep acronyms uppercase, lowercase the first letter of other periods
  const acronyms = ["YTD", "MTD", "QTD", "TTM"];
  return acronyms.includes(period)
    ? period
    : period.charAt(0).toLowerCase() + period.slice(1);
};

export const transformKPIResponse = (
  t: TFunction,
  response: Record<string, ReportData>,
  arrMrrLabel: string
): KPIDisplayProps[] => {
  const kpisData = response["kpis"];
  if (!kpisData) return [];

  const periodFromData = (
    data: SingleValueData | ComparisonData | PeerComparisonData
  ): string =>
    typeof data?.metadata?.period === "number"
      ? periodEnumToString(data.metadata.period)
      : "quarter";

  const infoTooltipKey = (key: string): string => `kpis.tooltip.${key}`;

  return Object.entries(kpisData)
    // `revenue` is a nested object rendered by the revenue KPI bar, not a card.
    .filter(([key]) => key !== "metadata" && key !== "revenue")
    .map(([key, data]) => {
      const unknownData = data as unknown;
      const kpiArray = Array.isArray(unknownData)
        ? (unknownData as SingleValueData[])
        : [data as SingleValueData];

      if (kpiArray.length > 1) {
        const period = formatPeriodCase(
          t(`timeframes.${periodFromData(kpiArray[0])}`)
        );
        return {
          title: t(`kpis.${key}`, {
            period,
            arrMrr: arrMrrLabel
          }),
          kpi: 0,
          kpiArray: kpiArray,
          isCurrency: kpiArray[0]?.metadata?.currency || false,
          info: i18next.exists(infoTooltipKey(key))
            ? t(infoTooltipKey(key), { period })
            : undefined,
          type: KPIDisplayType.NUMBER
        } as KPIDisplayProps;
      }

      const firstItem = kpiArray[0];
      if (!firstItem) {
        return {
          title: t(`kpis.${key}`),
          kpi: 0,
          isCurrency: false,
          type: KPIDisplayType.NUMBER
        } as KPIDisplayProps;
      }

      const period = formatPeriodCase(
        t(`timeframes.${periodFromData(firstItem)}`)
      );
      const baseProps: KPIDisplayProps = {
        title: t(`kpis.${key}`, {
          period,
          arrMrr: arrMrrLabel
        }),
        kpi: firstItem.value,
        isCurrency:
          typeof firstItem?.metadata?.currency === "boolean"
            ? firstItem.metadata.currency
            : false,
        info: i18next.exists(infoTooltipKey(key))
          ? t(infoTooltipKey(key), { period })
          : undefined
      };

      baseProps.type = KPIDisplayType.NUMBER;
      if (
        "comparedTo" in firstItem &&
        (firstItem as ComparisonData).comparedTo !== -1
      ) {
        baseProps.type = KPIDisplayType.PERCENTAGE;
        baseProps.kpiTotal = (firstItem as ComparisonData).comparedTo;
      }

      return baseProps;
    });
};

export const periodStringToEnum = (
  timeframe: SettingsPeriodString | string
): SettingsPeriod => {
  switch (timeframe) {
    case "week":
      return SettingsPeriod.WEEK;
    case "month":
      return SettingsPeriod.MONTH;
    case "quarter":
      return SettingsPeriod.QUARTER;
    case "year":
      return SettingsPeriod.YEAR;
    case "allTimes":
      return SettingsPeriod.ALL_TIMES;
    case "ytd":
      return SettingsPeriod.YTD;
    case "last12m":
      return SettingsPeriod.LAST_12_MONTHS;
    case "last24m":
      return SettingsPeriod.LAST_24_MONTHS;
    default:
      return SettingsPeriod.YEAR;
  }
};

export const periodEnumToString = (
  period: SettingsPeriod
): SettingsPeriodString => {
  switch (period) {
    case SettingsPeriod.WEEK:
      return "week";
    case SettingsPeriod.MONTH:
      return "month";
    case SettingsPeriod.QUARTER:
      return "quarter";
    case SettingsPeriod.YEAR:
      return "year";
    case SettingsPeriod.ROLLING_YEAR:
      return "rollingYear";
    case SettingsPeriod.ALL_TIMES:
      return "allTimes";
    case SettingsPeriod.YTD:
      return "ytd";
    case SettingsPeriod.LAST_12_MONTHS:
      return "last12m";
    case SettingsPeriod.LAST_24_MONTHS:
      return "last24m";
  }
};
